/**
 * Admin reports API module.
 *
 * Typed helpers for the three admin reports endpoints:
 *  - `GET /api/v1/admin/reports/sales` (revenue + order counts over time)
 *  - `GET /api/v1/admin/reports/products` (top products by units and revenue)
 *  - `GET /api/v1/admin/reports/production` (job throughput over time)
 *
 * All three require `auth:console` guard + `reports.view` Passport scope.
 * Callers (containers, Redux thunks) MUST import from this module — never
 * call the shared axios client directly.
 *
 * Wire shape: platform templates emit snake_case; each helper projects to
 * camelCase before returning the typed DTO.
 *
 * CON-REPORT.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  SalesReportDTO,
  SalesReportParams,
  ProductsReportDTO,
  ProductsReportParams,
  ProductionReportDTO,
  ProductionReportParams,
} from '@dfq/shared/types/reports';

// ---------------------------------------------------------------------------
// Wire shapes (snake_case from the platform)
// ---------------------------------------------------------------------------

interface WireSalesReportData {
  totals: {
    total_revenue_cents: number;
    net_revenue_cents: number;
    total_order_count: number;
    average_order_value_cents: number;
  };
  buckets: Array<{
    bucket: string;
    revenue_cents: number;
    order_count: number;
  }>;
}

interface WireProductsReportData {
  top_by_units: Array<{
    product_id: number | null;
    product_name: string;
    units_sold: number;
    revenue_cents: number;
  }>;
  top_by_revenue: Array<{
    product_id: number | null;
    product_name: string;
    units_sold: number;
    revenue_cents: number;
  }>;
}

interface WireProductionReportData {
  totals: {
    total_completed: number;
    total_failed: number;
  };
  jobs_by_status: Array<{
    status: string;
    count: number;
  }>;
  completed_buckets: Array<{
    bucket: string;
    completed_count: number;
  }>;
}

// ---------------------------------------------------------------------------
// Sales report
// ---------------------------------------------------------------------------

/**
 * Fetch revenue and order-count buckets plus aggregate totals.
 *
 * Route: `GET /api/v1/admin/reports/sales`
 * Auth:  `auth:console` + `reports.view` scope.
 *
 * @param params - Required `from`/`to` (YYYY-MM-DD) and optional `granularity`.
 */
export async function fetchSalesReport(params: SalesReportParams): Promise<SalesReportDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<WireSalesReportData>>(
      '/admin/reports/sales',
      { params },
    );
    const data = unwrap(res.data, res.status);
    return {
      totals: {
        totalRevenueCents: data.totals.total_revenue_cents,
        netRevenueCents: data.totals.net_revenue_cents,
        totalOrderCount: data.totals.total_order_count,
        averageOrderValueCents: data.totals.average_order_value_cents,
      },
      buckets: data.buckets.map((b) => ({
        bucket: b.bucket,
        revenueCents: b.revenue_cents,
        orderCount: b.order_count,
      })),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Products report
// ---------------------------------------------------------------------------

/**
 * Fetch top products ranked by units sold and by revenue.
 *
 * Route: `GET /api/v1/admin/reports/products`
 * Auth:  `auth:console` + `reports.view` scope.
 *
 * @param params - Required `from`/`to` and optional `limit` (1–100, default 25).
 */
export async function fetchProductsReport(params: ProductsReportParams): Promise<ProductsReportDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<WireProductsReportData>>(
      '/admin/reports/products',
      { params },
    );
    const data = unwrap(res.data, res.status);
    const mapItem = (item: WireProductsReportData['top_by_units'][number]) => ({
      productId: item.product_id,
      productName: item.product_name,
      unitsSold: item.units_sold,
      revenueCents: item.revenue_cents,
    });
    return {
      topByUnits: data.top_by_units.map(mapItem),
      topByRevenue: data.top_by_revenue.map(mapItem),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Production report
// ---------------------------------------------------------------------------

/**
 * Fetch production job throughput: WIP snapshot by status + completed
 * jobs bucketed over the date range.
 *
 * Route: `GET /api/v1/admin/reports/production`
 * Auth:  `auth:console` + `reports.view` scope.
 *
 * @param params - Required `from`/`to` and optional `granularity`.
 */
export async function fetchProductionReport(params: ProductionReportParams): Promise<ProductionReportDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<WireProductionReportData>>(
      '/admin/reports/production',
      { params },
    );
    const data = unwrap(res.data, res.status);
    return {
      totals: {
        totalCompleted: data.totals.total_completed,
        totalFailed: data.totals.total_failed,
      },
      jobsByStatus: data.jobs_by_status,
      completedBuckets: data.completed_buckets.map((b) => ({
        bucket: b.bucket,
        completedCount: b.completed_count,
      })),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}
